import React, { ChangeEvent, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import closeIcon from "../assests/close.svg";
import CustomInput from "../components/custom-input";
import CustomRadioButton from "../components/custom-radio-button";
import CustomSelect from "../components/custom-select";
import CustomButton from "../components/custom-button";
import FormSection from "../components/form-section";
import { ParseErrors } from "../utils/parse-errors";
import axiosInstance from "../utils/axios-instance";
import { toast } from "react-toastify";
import Loader from "../components/loader";
import { useDebounceCallback } from "usehooks-ts";
import { useChaseEditModel } from "../store/use-chase-edit-model";
import {
  brandCodes,
  branddata,
  loanStatusData,
} from "../components/chaseListComponents/chase-list-data";
import GetCancellationReason, {
  cancellationReasons,
} from "../utils/get-cancellation-reason";
import CanculateChaseOutstandingBalance from "../utils/canculate-chase-outstanding-balance";
import moment from "moment";
import GetUser from "../utils/get-user";

interface IEditChaseModel {
  UpdateItemData: () => void;
}

const EditChaseModel: React.FC<IEditChaseModel> = ({ UpdateItemData }) => {
  const user = GetUser();
  const { initialValues, onClose } = useChaseEditModel();
  const [values, setValues] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [outStandingAmount, setOutstandingAmount] = useState(0);

  // --------------------------------- [ HANDLE SUBMIT ]-------------------------------------

  const updateChase = async () => {
    if (validate()) {
      setLoading(true);
      try {
        await axiosInstance.patch(`/loan/${initialValues?.id}`, {
          ...values,
        });
        UpdateItemData();
        toast.success("Loan Updated Successfully");
        onClose();
        setLoading(false);
      } catch (error) {
        toast.error(ParseErrors(error));
        setLoading(false);
        console.log(error);
      }
    }
  };

  // ---------------------------------- [ UPDATE INPUT VALUES ] --------------------------------

  const HandleChangeValues = useDebounceCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    },
    200
  );

  const GetDefaultValues = (name: string) => {
    return (
      (typeof values[name] === "number"
        ? Number(values[name])?.toFixed(2)
        : values[name]) ||
      (typeof initialValues?.data[name] === "number"
        ? Number(initialValues?.data[name])?.toFixed(2)
        : initialValues?.data[name])
    );
  };

  // ------------------------------------------------------------------------- PERFORMING COUNT LOGIC -------------------------------------------------------

  let totalAmount = outStandingAmount
    ? Number(
        Math.abs(outStandingAmount) +
          Number(GetDefaultValues("partialPaymentAmount"))
      )
    : Number(GetDefaultValues("totalAmount"));

  useEffect(() => {
    setOutstandingAmount(
      CanculateChaseOutstandingBalance({
        insurerRefund: Number(GetDefaultValues("insurerRefund")) || 0,
        reclaimedInceptionDiscount:
          Number(GetDefaultValues("reclaimedInceptionDiscount")) || 0,
        cancelFee: Number(GetDefaultValues("cancelFee")) || 0,
        unusedAddOnsRefund: Number(GetDefaultValues("unusedAddOnsRefund")) || 0,
        outstandingBalanceofFinanceAgreement:
          Number(GetDefaultValues("outstandingBalanceofFinanceAgreement")) || 0,
        partialPaymentAmount:
          Number(GetDefaultValues("partialPaymentAmount")) || 0,
      })
    );
  }, [values]);

  // ------------------------------------------------------------------- UPDATING ORIGNAL VALUES ---------------------------------------------------------
  useEffect(() => {
    if (
      values?.insurerRefund ||
      values?.reclaimedInceptionDiscount ||
      values?.cancelFee ||
      values?.unusedAddOnsRefund ||
      values?.outstandingBalanceofFinanceAgreement ||
      values?.partialPaymentAmount
    ) {
      setValues((prev) => ({
        ...prev,
        outstandingBalance: outStandingAmount,
        totalAmount: totalAmount,
      }));
    }
  }, [outStandingAmount]);

  const validate = () => {
    if (
      values?.status === "On-Hold" &&
      !values?.restartDate &&
      !initialValues?.data?.restartDate
    ) {
      toast.error("Restart Date is required if you want to put chase on hold");
      return false;
    } else {
      return true;
    }
  };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [loading, onClose]);

  return createPortal(
    <div
      className="z-[99999] w-screen h-screen fixed inset-0 bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 animate-fade-in"
      onClick={() => !loading && onClose()}
    >
      <div
        className="bg-surface border border-edge shadow-overlay w-[850px] rounded-2xl animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/*---------------------------- [ HEADER ] ------------------------------- */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-edge-faint">
          <h2 className="text-[19px] font-bold text-heading-color tracking-tight">Update Chase</h2>
          <button onClick={onClose} className="h-8 w-8 rounded-lg flex items-center justify-center border border-edge bg-overlay text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-all duration-200">
            <img
              src={closeIcon}
              alt="Close Img"
              className="h-3.5 w-3.5 object-contain cursor-pointer"
            />
          </button>
        </div>
        {/*---------------------------- [ HEADER ] ------------------------------- */}

        {/* -------------------------- [ BODY ] ------------------------------------ */}
        <div className="px-6 py-6 flex flex-col gap-5">
          <FormSection
            title="Customer"
            description="Who the chase is for and how to reach them."
          >
            <div className="contents">
              <CustomInput
                autoComplete="none"
                label="First Name"
                defaultValue={GetDefaultValues("firstName")}
                name="firstName"
                onChange={HandleChangeValues}
              ></CustomInput>
              <CustomInput
                autoComplete="none"
                label="Surname"
                defaultValue={GetDefaultValues("surname")}
                name="surname"
                onChange={HandleChangeValues}
              ></CustomInput>
            </div>
            <div className="contents">
              <CustomInput
                label="Email Address"
                autoComplete="none"
                defaultValue={GetDefaultValues("email")}
                name="email"
                onChange={HandleChangeValues}
              ></CustomInput>
              <CustomInput
                label="Phone Number"
                autoComplete="none"
                defaultValue={GetDefaultValues("phone")}
                name="phone"
                onChange={HandleChangeValues}
              ></CustomInput>
            </div>

            <div className="contents">
              <CustomInput
                label="Date of Birth"
                autoComplete="none"
                defaultValue={GetDefaultValues("dob")}
                name="dob"
                type="date"
                onChange={HandleChangeValues}
              ></CustomInput>
              <CustomInput
                label="Post Code"
                autoComplete="none"
                defaultValue={GetDefaultValues("postCode")}
                name="postCode"
                className="uppercase"
                onChange={HandleChangeValues}
              ></CustomInput>
            </div>

          </FormSection>

          <FormSection title="Address">

            <div className="contents">
              <CustomInput
                label="Address 1"
                autoComplete="none"
                defaultValue={GetDefaultValues("address1")}
                name="address1"
                onChange={HandleChangeValues}
              ></CustomInput>
              <CustomInput
                label="Address 2"
                autoComplete="none"
                defaultValue={GetDefaultValues("address2")}
                name="address2"
                onChange={HandleChangeValues}
              ></CustomInput>
            </div>
            <div className="contents">
              <CustomInput
                label="Address 3"
                autoComplete="none"
                defaultValue={GetDefaultValues("address3")}
                name="address3"
                onChange={HandleChangeValues}
              ></CustomInput>
              <CustomInput
                label="Address 4"
                autoComplete="none"
                defaultValue={GetDefaultValues("address4")}
                name="address4"
                onChange={HandleChangeValues}
              ></CustomInput>
            </div>
            <div className="contents">
              <CustomInput
                label="Address 5"
                autoComplete="none"
                defaultValue={GetDefaultValues("address5")}
                name="address5"
                onChange={HandleChangeValues}
              ></CustomInput>

              <div className="w-full">
                <CustomSelect
                  comboboxType="select"
                  label="Reason For Cancellation"
                  labelProperty="label"
                  showTop
                  valueProperty="code"
                  options={cancellationReasons}
                  onSingleSelectChange={(option) =>
                    setValues((prev) => ({
                      ...prev,
                      reasonForCancellation: option?.code,
                    }))
                  }
                  value={GetCancellationReason(
                    GetDefaultValues("reasonForCancellation")
                  )}
                />
              </div>
            </div>

          </FormSection>

          <FormSection title="Policy & Brand">
          <div className="contents">
            <div className="w-full">
              <CustomSelect
                comboboxType="select"
                label="Chase Status"
                labelProperty="name"
                showTop
                valueProperty="name"
                options={loanStatusData}
                onSingleSelectChange={(option) =>
                  setValues((prev) => ({ ...prev, status: option?.name }))
                }
                value={loanStatusData.find(
                  (type) => type.name === GetDefaultValues("status")
                )}
                disabled={
                  (initialValues?.data?.log?.attempt <
                    Number(
                      initialValues.data?.log?.paymentPlan.split("-")[0]
                    ) &&
                    initialValues.data.status === "On-Hold") ||
                  (initialValues?.data?.status === "Debt-Collectors" &&
                    !["4W", "CD"]?.includes(user?.code))
                }
              />

              {GetDefaultValues("status") === "On-Hold" ? (
                <div className="mt-4">
                  <CustomInput
                    label="Restart Date"
                    autoComplete="none"
                    defaultValue={moment(
                      GetDefaultValues("restartDate")
                    ).format("YYYY-MM-DD")}
                    name="restartDate"
                    type="date"
                    min={moment(
                      new Date().setDate(new Date().getDate() + 1)
                    ).format("YYYY-MM-DD")}
                    onChange={HandleChangeValues}
                  ></CustomInput>
                </div>
              ) : null}
            </div>

            <div className="w-full">
              <CustomSelect
                comboboxType="select"
                label="Brand"
                labelProperty="label"
                showTop
                valueProperty="name"
                options={branddata}
                onSingleSelectChange={(option) =>
                  setValues((prev) => ({ ...prev, brand: option?.name }))
                }
                value={branddata.find(
                  (type) => type.name === GetDefaultValues("brand")
                )}
              />
            </div>
          </div>

          <div className="contents">
            <div className="w-full">
              <CustomSelect
                comboboxType="select"
                label="Affinity"
                labelProperty="value"
                showTop
                valueProperty="value"
                options={brandCodes}
                onSingleSelectChange={(option) =>
                  setValues((prev) => ({ ...prev, brandCode: option?.value }))
                }
                value={brandCodes.find(
                  (type) => type.value === GetDefaultValues("brandCode")
                )}
              />
            </div>

            <CustomInput
              label="Agent Code"
              autoComplete="none"
              defaultValue={GetDefaultValues("schemeCode")}
              name="schemeCode"
              onChange={HandleChangeValues}
            ></CustomInput>
          </div>

          <div className="contents">
            <CustomInput
              label="Policy Reference Number"
              autoComplete="none"
              defaultValue={GetDefaultValues("policyReference")}
              name="policyReference"
              onChange={HandleChangeValues}
            ></CustomInput>

            <CustomInput
              label="Insurer Name"
              autoComplete="none"
              defaultValue={GetDefaultValues("insurerName")}
              name="insurerName"
              onChange={HandleChangeValues}
            ></CustomInput>
          </div>

          <div className="contents">
            <CustomInput
              label="Vechile Reg No"
              autoComplete="none"
              defaultValue={GetDefaultValues("vehicleReg")}
              name="vehicleReg"
              onChange={HandleChangeValues}
              className="uppercase font-heading-font"
            ></CustomInput>
          </div>

          </FormSection>

          <FormSection title="Amounts">
          <div className="contents">
            <CustomInput
              label="Insurer Refund"
              autoComplete="none"
              defaultValue={GetDefaultValues("insurerRefund")}
              name="insurerRefund"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              disabled={
                GetDefaultValues("status") !== "Awaiting-Customer" &&
                !["SA", "CR"]?.includes(user?.code)
              }
            ></CustomInput>

            <CustomInput
              label="Reclaimed Inception Discount"
              autoComplete="none"
              defaultValue={GetDefaultValues("reclaimedInceptionDiscount")}
              name="reclaimedInceptionDiscount"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              negativeLabel
              disabled={
                GetDefaultValues("status") !== "Awaiting-Customer" &&
                !["SA", "CR"]?.includes(user?.code)
              }
            ></CustomInput>
          </div>

          <div className="contents">
            <CustomInput
              label="Outstanding Balance of Finance Agreement"
              autoComplete="none"
              defaultValue={GetDefaultValues(
                "outstandingBalanceofFinanceAgreement"
              )}
              name="outstandingBalanceofFinanceAgreement"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              negativeLabel
              disabled={
                GetDefaultValues("status") !== "Awaiting-Customer" &&
                !["SA", "CR"]?.includes(user?.code)
              }
            ></CustomInput>
            <CustomInput
              label="Cancellation Fee"
              autoComplete="none"
              defaultValue={GetDefaultValues("cancelFee")}
              name="cancelFee"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              negativeLabel
              disabled={
                GetDefaultValues("status") !== "Awaiting-Customer" &&
                !["SA", "CR"]?.includes(user?.code)
              }
            ></CustomInput>
          </div>

          <div className="contents">
            <CustomInput
              label="Partial Payment Amount"
              autoComplete="none"
              defaultValue={GetDefaultValues("partialPaymentAmount")}
              name="partialPaymentAmount"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              disabled={
                GetDefaultValues("status") !== "Awaiting-Customer" &&
                !["SA", "CR"]?.includes(user?.code)
              }
            ></CustomInput>

            <CustomInput
              label="Commissions/Addons Refund"
              autoComplete="none"
              defaultValue={GetDefaultValues("unusedAddOnsRefund")}
              name="unusedAddOnsRefund"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              disabled={
                GetDefaultValues("status") !== "Awaiting-Customer" &&
                !["SA", "CR"]?.includes(user?.code)
              }
            ></CustomInput>
          </div>
          <div className="contents">
            <CustomInput
              label="Total Amount"
              autoComplete="none"
              value={Math.abs(totalAmount)?.toFixed(2)}
              name="totalAmount"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              disabled={true}
            ></CustomInput>

            <CustomInput
              label="Outstanding Balance"
              autoComplete="none"
              value={
                Math.abs(outStandingAmount)?.toFixed(2) ||
                GetDefaultValues("outstandingBalance")
              }
              name="outstandingBalance"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              negativeLabel={outStandingAmount < 0}
              disabled={true}
              error={
                Number(outStandingAmount.toFixed(2)) > 0
                  ? "Invalid Outstanding Balance"
                  : null
              }
            ></CustomInput>
          </div>

          </FormSection>

          <FormSection title="Classification">
          <div className="contents">
            <div className="w-full">
              <label className="text-label-muted font-medium block text-sm leading-[16.98px]">
                Policy Type
              </label>
              <div className="w-full flex items-center gap-5 my-2">
                <div className="w-full flex gap-5 ">
                  <CustomRadioButton
                    id="standard"
                    name="policyType"
                    label="Standard"
                    checked={GetDefaultValues("policyType") === "Standard"}
                    onChange={HandleChangeValues}
                    value="Standard"
                  />
                  <CustomRadioButton
                    id="Nonstandard"
                    name="policyType"
                    label="Non-Standard"
                    value="Non-Standard"
                    checked={GetDefaultValues("policyType") === "Non-Standard"}
                    onChange={HandleChangeValues}
                  />
                </div>
              </div>
            </div>
            <div className="w-full">
              <label className="text-label-muted font-medium block text-sm leading-[16.98px]">
                Department{" "}
              </label>
              <div className="w-full flex gap-5 my-2">
                <CustomRadioButton
                  id="sales"
                  name="bussinessType"
                  label="Sales"
                  checked={GetDefaultValues("bussinessType") === "Sales"}
                  onChange={HandleChangeValues}
                  value="Sales"
                />
                <CustomRadioButton
                  id="Renewals"
                  name="bussinessType"
                  label="Renewals"
                  value="Renewals"
                  checked={GetDefaultValues("bussinessType") === "Renewals"}
                  onChange={HandleChangeValues}
                />
              </div>
            </div>
          </div>

          </FormSection>
        </div>
        <div className="border-t border-edge-faint px-6 py-5 flex items-center justify-end gap-3">
          <CustomButton
            className="bg-surface border border-edge hover:border-edge-strong cursor-pointer rounded-xl h-11 px-6 font-semibold !text-[14px] text-heading-color transition-colors duration-200"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </CustomButton>
          <CustomButton
            className="bg-gradient-to-r from-accent to-accent-bright shadow-[0_8px_20px_-8px_rgba(99,102,241,0.65)] hover:-translate-y-px cursor-pointer rounded-xl h-11 px-6 font-semibold !text-[14px] text-white transition-all duration-200"
            onClick={updateChase}
            disabled={loading || Number(outStandingAmount.toFixed(2)) > 0}
          >
            {loading ? <Loader /> : "Update Loan"}
          </CustomButton>
        </div>
        {/* -------------------------- [ BODY ] ------------------------------------ */}
      </div>
    </div>,
    document.body
  );
};

export default EditChaseModel;
